import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const ProjectGanttChart = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const chartRef = useRef(null);
  
  // Project timeline: Feb 2026 - Aug 2027 (18 months + 6 months float)
  // Also showing pre-project milestone (Jan 2026)
  const startDate = new Date(2025, 10, 1); // Nov 1, 2025 (showing 3 months before project)
  const months = 27; // 3 months pre-project + 18 months project + 6 months float
  const projectStartMonth = 3; // Project officially starts at month 3 (Feb 2026)
  
  // Pre-project tasks and milestones
  const preProjectPhase = {
    name: "Pre-Project Activities",
    startMonth: 0, // Nov 2025
    duration: 3,
    trl: "TRL 3-4 → 5-6",
    cost: "Prior Funding",
    tasks: [
      { name: "AlphaFold Integration Development", startMonth: 0, duration: 2 },
      { name: "MechInterp Core Enhancements", startMonth: 1, duration: 2 },
      { name: "Milestone: Contract Execution & AlphaFold Integration", startMonth: 3, duration: 0, isMilestone: true }
    ]
  };
  
  // Project phases and tasks
  const phases = [
    preProjectPhase,
    {
      name: "Phase 1: Enhanced Interpretability",
      startMonth: 3, // Feb 2026
      duration: 4,
      trl: "TRL 5-6",
      cost: "$280,000",
      tasks: [
        { name: "Enhanced Visualization for AlphaFold Models", startMonth: 3, duration: 3 },
        { name: "Core UI Refinement", startMonth: 3, duration: 3 },
        { name: "Interpretability Engine Expansion", startMonth: 4, duration: 3 },
        { name: "Milestone: Enhanced Interpretability Complete", startMonth: 7, duration: 0, isMilestone: true }
      ]
    },
    {
      name: "Phase 2: Advanced Functionality",
      startMonth: 7, // Jun 2026
      duration: 7,
      trl: "TRL 6-7",
      cost: "$340,000",
      tasks: [
        { name: "Molecular Docking Integration", startMonth: 7, duration: 4 },
        { name: "Simulation Engine Development", startMonth: 8, duration: 5 },
        { name: "Advanced Visualization Tools", startMonth: 9, duration: 5 },
        { name: "Milestone: Feature Complete Beta", startMonth: 14, duration: 0, isMilestone: true }
      ]
    },
    {
      name: "Phase 3: Validation & Optimization",
      startMonth: 14, // Jan 2027
      duration: 4,
      trl: "TRL 7",
      cost: "$150,000",
      tasks: [
        { name: "UOW Research Team Validation", startMonth: 14, duration: 3 },
        { name: "Performance Optimization", startMonth: 15, duration: 2 },
        { name: "User Feedback Implementation", startMonth: 16, duration: 2 },
        { name: "Milestone: System Validation Complete", startMonth: 18, duration: 0, isMilestone: true }
      ]
    },
    {
      name: "Phase 4: Commercial Launch",
      startMonth: 18, // May 2027
      duration: 3,
      trl: "TRL 7-8",
      cost: "$80,000",
      tasks: [
        { name: "Market Preparation", startMonth: 18, duration: 2 },
        { name: "Documentation & Training Materials", startMonth: 18, duration: 2 },
        { name: "Initial Customer Onboarding", startMonth: 19, duration: 2 },
        { name: "Milestone: Commercial Beta Release", startMonth: 21, duration: 0, isMilestone: true }
      ]
    },
    {
      name: "Contingency & Follow-up",
      startMonth: 21, // Aug 2027
      duration: 6,
      trl: "TRL 8",
      cost: "Float Period",
      tasks: [
        { name: "Contingency Buffer", startMonth: 21, duration: 3 },
        { name: "Follow-up Support & Refinement", startMonth: 24, duration: 3 }
      ]
    }
  ];
  
  // Generate month labels - only showing key months for clarity
  const monthLabels = [];
  for (let i = 0; i < months; i++) {
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + i);
    monthLabels.push(date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }));
  }
  
  // Colors for phases
  const phaseColors = [
    'bg-gray-400', // Pre-project phase
    'bg-blue-300',
    'bg-indigo-300',
    'bg-purple-300',
    'bg-pink-300',
    'bg-gray-300' // Contingency
  ];
  
  const taskColors = [
    'bg-gray-300', // Pre-project tasks
    'bg-blue-200',
    'bg-indigo-200',
    'bg-purple-200',
    'bg-pink-200',
    'bg-gray-200' // Contingency tasks
  ];
  
  // Calculate bar positions and widths
  const calculateBarStyle = (startMonth, duration) => {
    const leftPosition = (startMonth / months) * 100;
    const width = (duration / months) * 100;
    return { left: `${leftPosition}%`, width: `${width}%` };
  };

  // Export the chart as a PDF (optimized for better handling)
  const handleExportPDF = async () => {
    if (!chartRef.current || isExporting) return;

    try {
      setIsExporting(true);
      setProgress(10);
      
      // Temporarily apply print-specific styles
      const chartElement = chartRef.current;
      chartElement.classList.add('printing');
      
      // Use html2canvas with specific options for better rendering
      const canvas = await html2canvas(chartElement, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        // Focus on just the chart content
        onclone: (document, element) => {
          // Any additional DOM manipulations on the cloned element if needed
          element.style.height = 'auto';
          element.style.overflow = 'visible';
        }
      });
      
      // Remove print styles
      chartElement.classList.remove('printing');
      
      setProgress(70);
      
      // Create PDF with appropriate dimensions
      // Use A3 landscape for more space
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a3'
      });
      
      // Get canvas dimensions and PDF dimensions
      const imgWidth = 420; // A3 width in mm
      const imgHeight = 297; // A3 height in mm
      
      // Add the image to the PDF
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        0,
        0,
        imgWidth,
        imgHeight
      );
      
      setProgress(90);
      
      // Save the PDF
      pdf.save('mechinterp-project-plan.pdf');
      
      setProgress(100);
      
      setTimeout(() => {
        setIsExporting(false);
        setProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setIsExporting(false);
      setProgress(0);
      alert('Failed to export PDF. Please try again. Error: ' + error.message);
    }
  };

  // Export as PNG
  const handleExportPNG = async () => {
    if (!chartRef.current || isExporting) return;

    try {
      setIsExporting(true);
      setProgress(10);
      
      // Temporarily apply print-specific styles
      const chartElement = chartRef.current;
      chartElement.classList.add('printing');
      
      // Use html2canvas with specific options for better rendering
      const canvas = await html2canvas(chartElement, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        // Focus on just the chart content
        onclone: (document, element) => {
          // Any additional DOM manipulations on the cloned element if needed
          element.style.height = 'auto';
          element.style.overflow = 'visible';
        }
      });
      
      // Remove print styles
      chartElement.classList.remove('printing');
      
      setProgress(80);
      
      // Convert to data URL and download
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'mechinterp-project-plan.png';
      link.href = dataUrl;
      link.click();
      
      setProgress(100);
      
      setTimeout(() => {
        setIsExporting(false);
        setProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to export PNG:', error);
      setIsExporting(false);
      setProgress(0);
      alert('Failed to export PNG. Please try again. Error: ' + error.message);
    }
  };

  // Print-specific CSS for @media print
  const printStyles = `
    @media print {
      .printing {
        width: 100% !important;
        max-width: none !important;
      }
      .printing * {
        color-adjust: exact !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;

  return (
    <div className="w-full bg-gray-100 min-h-screen flex flex-col items-center py-8">
      {/* Inject print styles */}
      <style>{printStyles}</style>
      
      {/* Controls */}
      <div className="w-full max-w-4xl mb-8 px-4">
        <div className="flex flex-wrap gap-4 mb-4">
          <button 
            onClick={handleExportPNG}
            disabled={isExporting}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            Export as PNG
          </button>
          <button 
            onClick={handleExportPDF}
            disabled={isExporting}
            className="px-6 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            Export as PDF (A3 Landscape)
          </button>
        </div>
        
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="text-lg font-bold text-yellow-700 mb-2">Format Information</h3>
          <p className="text-yellow-700">
            The chart will be exported as an A3 landscape document (420mm × 297mm) to ensure all project phases fit on a single page.
            You can print this on A3 paper or scale it down to A4 for your BioSF application.
          </p>
        </div>
        
        {isExporting && (
          <div className="mt-2">
            <div className="w-full bg-gray-300 h-2 rounded-full overflow-hidden">
              <div 
                className="bg-blue-600 h-full transition-all duration-300 ease-out" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-1">Exporting: {progress}% complete</p>
          </div>
        )}
      </div>
      
      {/* Gantt Chart */}
      <div className="max-w-full overflow-auto border border-gray-300 rounded-lg shadow-lg bg-white">
        <div 
          ref={chartRef}
          className="p-8 w-[1440px]" /* Fixed width for consistent export */
        >
          {/* Title and Stats */}
          <div className="flex flex-wrap justify-between items-center mb-6 border-b border-gray-200 pb-4">
            <div className="mr-4">
              <h1 className="text-2xl font-bold text-gray-800 mb-1">MechInterp BioSynth Suite - Project Plan</h1>
              <h2 className="text-lg text-gray-600">February 2026 Project Start (with contingency to Feb 2028)</h2>
            </div>
            <div className="flex flex-wrap gap-6 mt-2 md:mt-0">
              <div className="px-2">
                <div className="font-bold text-gray-700">Total Budget</div>
                <div className="text-gray-900">$850,000</div>
              </div>
              <div className="px-2">
                <div className="font-bold text-gray-700">Project Duration</div>
                <div className="text-gray-900">18 Months + 6 Months Float</div>
              </div>
              <div className="px-2">
                <div className="font-bold text-gray-700">Starting TRL</div>
                <div className="text-gray-900">TRL 5-6 (Feb 2026)</div>
              </div>
              <div className="px-2">
                <div className="font-bold text-gray-700">Target TRL</div>
                <div className="text-gray-900">TRL 7-8</div>
              </div>
            </div>
          </div>
          
          {/* Month Labels */}
          <div className="flex mb-4">
            <div className="w-1/4 pr-4">
              <div className="font-bold text-gray-700">Project Phases</div>
            </div>
            <div className="w-3/4 relative">
              <div className="flex justify-between mb-1">
                {monthLabels.filter((_, i) => i % 3 === 0).map((label, i) => (
                  <div key={i} className="text-xs font-medium text-gray-500 whitespace-nowrap" style={{ width: '25%', textAlign: 'center' }}>
                    {label}
                  </div>
                ))}
              </div>
              <div className="h-1 w-full bg-gray-200 rounded"></div>
              
              {/* Project start line */}
              <div className="absolute h-full border-l-2 border-red-500 border-dashed" style={{ left: `${(projectStartMonth / months) * 100}%`, top: '10px' }}></div>
            </div>
          </div>
          
          {/* Phases and Tasks */}
          {phases.map((phase, phaseIndex) => (
            <div key={phaseIndex} className="mb-6">
              <div className="flex mb-2">
                <div className="w-1/4 pr-4">
                  <div className="font-bold text-gray-800">{phase.name}</div>
                  <div className="text-sm text-gray-600 mt-1">TRL: {phase.trl}</div>
                  <div className="text-sm text-gray-600">Budget: {phase.cost}</div>
                </div>
                <div className="w-3/4 relative h-8">
                  <div 
                    className={`absolute h-8 ${phaseColors[phaseIndex]} rounded opacity-60 ${phaseIndex === 0 ? 'border-2 border-dashed border-gray-500' : ''}`}
                    style={calculateBarStyle(phase.startMonth, phase.duration)}
                  ></div>
                </div>
              </div>
              
              {/* Tasks for this phase */}
              {phase.tasks.map((task, taskIndex) => (
                <div key={taskIndex} className="flex mb-1">
                  <div className="w-1/4 pr-4 pl-4">
                    <div className={`text-sm ${task.isMilestone ? 'font-bold' : ''}`}>
                      {task.isMilestone && '♦️ '}{task.name}
                    </div>
                  </div>
                  <div className="w-3/4 relative h-6">
                    {task.isMilestone ? (
                      <div 
                        className="absolute w-6 h-6 bg-yellow-400 rotate-45 transform -translate-y-1/2 -translate-x-1/2"
                        style={{ left: `${(task.startMonth / months) * 100}%`, top: '50%' }}
                      ></div>
                    ) : (
                      <div 
                        className={`absolute h-6 ${taskColors[phaseIndex]} rounded opacity-80 ${phaseIndex === 0 ? 'border-2 border-dashed border-gray-500' : ''}`}
                        style={calculateBarStyle(task.startMonth, task.duration)}
                      ></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
          
          {/* Budget Table */}
          <div className="mt-8 border-t-2 border-gray-200 pt-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Project Budget Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-700">
                <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Cost Category</th>
                    <th className="px-3 py-2 text-right">Phase 1<br/>($280,000)</th>
                    <th className="px-3 py-2 text-right">Phase 2<br/>($340,000)</th>
                    <th className="px-3 py-2 text-right">Phase 3<br/>($150,000)</th>
                    <th className="px-3 py-2 text-right">Phase 4<br/>($80,000)</th>
                    <th className="px-3 py-2 text-right font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white border-b">
                    <td className="px-3 py-2 font-medium">Software Development Labor</td>
                    <td className="px-3 py-2 text-right">$220,000</td>
                    <td className="px-3 py-2 text-right">$265,000</td>
                    <td className="px-3 py-2 text-right">$95,000</td>
                    <td className="px-3 py-2 text-right">$40,000</td>
                    <td className="px-3 py-2 text-right font-bold">$620,000</td>
                  </tr>
                  <tr className="bg-gray-50 border-b">
                    <td className="px-3 py-2 font-medium">User Testing & Validation</td>
                    <td className="px-3 py-2 text-right">$30,000</td>
                    <td className="px-3 py-2 text-right">$45,000</td>
                    <td className="px-3 py-2 text-right">$40,000</td>
                    <td className="px-3 py-2 text-right">$15,000</td>
                    <td className="px-3 py-2 text-right font-bold">$130,000</td>
                  </tr>
                  <tr className="bg-white border-b">
                    <td className="px-3 py-2 font-medium">Computing Infrastructure & Hardware</td>
                    <td className="px-3 py-2 text-right">$15,000</td>
                    <td className="px-3 py-2 text-right">$20,000</td>
                    <td className="px-3 py-2 text-right">$5,000</td>
                    <td className="px-3 py-2 text-right">$0</td>
                    <td className="px-3 py-2 text-right font-bold">$40,000</td>
                  </tr>
                  <tr className="bg-gray-50 border-b">
                    <td className="px-3 py-2 font-medium">Specialized Libraries & Tools</td>
                    <td className="px-3 py-2 text-right">$10,000</td>
                    <td className="px-3 py-2 text-right">$10,000</td>
                    <td className="px-3 py-2 text-right">$5,000</td>
                    <td className="px-3 py-2 text-right">$0</td>
                    <td className="px-3 py-2 text-right font-bold">$25,000</td>
                  </tr>
                  <tr className="bg-white border-b">
                    <td className="px-3 py-2 font-medium">Documentation & Commercialization</td>
                    <td className="px-3 py-2 text-right">$5,000</td>
                    <td className="px-3 py-2 text-right">$0</td>
                    <td className="px-3 py-2 text-right">$5,000</td>
                    <td className="px-3 py-2 text-right">$25,000</td>
                    <td className="px-3 py-2 text-right font-bold">$35,000</td>
                  </tr>
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right">$280,000</td>
                    <td className="px-3 py-2 text-right">$340,000</td>
                    <td className="px-3 py-2 text-right">$150,000</td>
                    <td className="px-3 py-2 text-right">$80,000</td>
                    <td className="px-3 py-2 text-right">$850,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Legend */}
          <div className="mt-6 flex flex-wrap items-center text-sm text-gray-700">
            <div className="font-bold mr-4">Legend:</div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-gray-300 rounded mr-1 opacity-60 border border-dashed border-gray-500"></div>
              <span>Pre-Project Activities</span>
            </div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-blue-200 rounded mr-1 opacity-80"></div>
              <span>Phase 1 Tasks</span>
            </div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-indigo-200 rounded mr-1 opacity-80"></div>
              <span>Phase 2 Tasks</span>
            </div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-purple-200 rounded mr-1 opacity-80"></div>
              <span>Phase 3 Tasks</span>
            </div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-pink-200 rounded mr-1 opacity-80"></div>
              <span>Phase 4 Tasks</span>
            </div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-gray-200 rounded mr-1 opacity-80"></div>
              <span>Contingency</span>
            </div>
            <div className="flex items-center mr-4">
              <div className="w-4 h-4 bg-yellow-400 rotate-45 mr-1"></div>
              <span>Milestones</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 border-l-2 border-red-500 border-dashed mr-1"></div>
              <span>Project Start</span>
            </div>
          </div>
          
          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center text-gray-500 text-sm">
            <div>BioSF Round 2 Application - April 2025</div>
            <div>ISD.ai - MechInterp BioSynth Suite</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectGanttChart;